// Analytics Data Engine for Smart RFQ System
// Extended mock dataset + computation functions

export type ClientType = 'operator' | 'epc' | 'vendor';

export interface AnalyticsRFQ {
  id: string;
  service: string;
  serviceLabel: string;
  status: 'submitted' | 'under-review' | 'validation-completed' | 'proposal-generated' | 'pending-feedback' | 'closed';
  priority: 'high' | 'medium' | 'low';
  date: string;
  client: string;
  company: string;
  country: string;
  countryLabel: string;
  clientType: ClientType;
  wellheadPressure: string;
  h2sPresence: string;
  sandProduction: string;
  timeline: string;
  duration: string;
  equipmentCount: number;
  completeness: number; // 0-100
  responseTimeHours: number;
  validationStatus: 'Approved' | 'Conditional' | 'Rejected' | null;
  proposalGenerated: boolean;
  clientResponded: boolean;
  revisionRequests: number;
}

// Extended mock dataset (30 days of RFQ data)
export const ANALYTICS_RFQS: AnalyticsRFQ[] = [
  {
    id: 'EP-2N4K8M', service: 'surface-well-testing', serviceLabel: 'Surface Well Testing',
    status: 'proposal-generated', priority: 'high', date: '2026-04-28', client: 'Ahmed Al-Rashid',
    company: 'National Energy Corp', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'operator',
    wellheadPressure: '7500', h2sPresence: 'yes', sandProduction: 'high', timeline: 'immediate',
    duration: 'long-term', equipmentCount: 14, completeness: 95, responseTimeHours: 18,
    validationStatus: 'Conditional', proposalGenerated: true, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-3F7R2P', service: 'stimulation-nitrogen', serviceLabel: 'Stimulation & Nitrogen',
    status: 'under-review', priority: 'medium', date: '2026-04-29', client: 'James Patterson',
    company: 'Gulf Petroleum Services', country: 'uae', countryLabel: 'UAE', clientType: 'epc',
    wellheadPressure: '4200', h2sPresence: 'no', sandProduction: 'low', timeline: '1-2-weeks',
    duration: 'short-term', equipmentCount: 8, completeness: 88, responseTimeHours: 24,
    validationStatus: null, proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-5H9T1W', service: 'fabrication-skid', serviceLabel: 'Fabrication & Skid Assembly',
    status: 'pending-feedback', priority: 'high', date: '2026-04-25', client: 'Khalid Mansour',
    company: 'EPTCO Engineering', country: 'egypt', countryLabel: 'Egypt', clientType: 'epc',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: 'planned',
    duration: 'long-term', equipmentCount: 5, completeness: 72, responseTimeHours: 36,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: false, revisionRequests: 1,
  },
  {
    id: 'EP-7K2M4X', service: 'logistics-transport', serviceLabel: 'Logistics & Transportation',
    status: 'submitted', priority: 'low', date: '2026-04-30', client: 'Omar Nasser',
    company: 'Desert Operations LLC', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'vendor',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: '1-2-weeks',
    duration: 'short-term', equipmentCount: 3, completeness: 65, responseTimeHours: 0,
    validationStatus: null, proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-9N1Q6Z', service: 'chemicals-supply', serviceLabel: 'Chemicals Supply',
    status: 'closed', priority: 'medium', date: '2026-04-20', client: 'Sarah Mitchell',
    company: 'Apex Energy Solutions', country: 'uae', countryLabel: 'UAE', clientType: 'operator',
    wellheadPressure: '', h2sPresence: 'yes', sandProduction: 'medium', timeline: 'planned',
    duration: 'long-term', equipmentCount: 4, completeness: 92, responseTimeHours: 12,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: true, revisionRequests: 0,
  },
  {
    id: 'EP-1A3B5C', service: 'surface-well-testing', serviceLabel: 'Surface Well Testing',
    status: 'closed', priority: 'high', date: '2026-04-05', client: 'Faisal Bin Saleh',
    company: 'Arabian Drilling', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'operator',
    wellheadPressure: '9800', h2sPresence: 'yes', sandProduction: 'high', timeline: 'immediate',
    duration: 'long-term', equipmentCount: 18, completeness: 100, responseTimeHours: 8,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: true, revisionRequests: 0,
  },
  {
    id: 'EP-2D4E6F', service: 'stimulation-nitrogen', serviceLabel: 'Stimulation & Nitrogen',
    status: 'closed', priority: 'medium', date: '2026-04-08', client: 'David Thompson',
    company: 'Schlumberger ME', country: 'uae', countryLabel: 'UAE', clientType: 'vendor',
    wellheadPressure: '3200', h2sPresence: 'no', sandProduction: 'low', timeline: '1-2-weeks',
    duration: 'short-term', equipmentCount: 6, completeness: 85, responseTimeHours: 22,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: true, revisionRequests: 1,
  },
  {
    id: 'EP-3G5H7I', service: 'equipment-rental', serviceLabel: 'Equipment Rental',
    status: 'proposal-generated', priority: 'low', date: '2026-04-12', client: 'Mohamed Farid',
    company: 'Petrojam Services', country: 'egypt', countryLabel: 'Egypt', clientType: 'epc',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: 'planned',
    duration: 'short-term', equipmentCount: 10, completeness: 78, responseTimeHours: 48,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-4J6K8L', service: 'surface-well-testing', serviceLabel: 'Surface Well Testing',
    status: 'under-review', priority: 'high', date: '2026-04-30', client: 'Abdullah Al-Dosari',
    company: 'Saudi Aramco — Field Ops', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'operator',
    wellheadPressure: '10500', h2sPresence: 'yes', sandProduction: 'high', timeline: 'immediate',
    duration: 'long-term', equipmentCount: 22, completeness: 98, responseTimeHours: 4,
    validationStatus: null, proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-5M7N9O', service: 'filtration-water', serviceLabel: 'Filtration & Water Treatment',
    status: 'validation-completed', priority: 'medium', date: '2026-04-18', client: 'Rajesh Patel',
    company: 'ADNOC Onshore', country: 'uae', countryLabel: 'UAE', clientType: 'operator',
    wellheadPressure: '2800', h2sPresence: 'no', sandProduction: 'medium', timeline: '1-2-weeks',
    duration: 'long-term', equipmentCount: 7, completeness: 90, responseTimeHours: 16,
    validationStatus: 'Approved', proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-6P8Q0R', service: 'fabrication-skid', serviceLabel: 'Fabrication & Skid Assembly',
    status: 'closed', priority: 'high', date: '2026-04-10', client: 'Hassan El-Naggar',
    company: 'Enppi', country: 'egypt', countryLabel: 'Egypt', clientType: 'epc',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: 'planned',
    duration: 'long-term', equipmentCount: 8, completeness: 100, responseTimeHours: 14,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: true, revisionRequests: 2,
  },
  {
    id: 'EP-7S9T1U', service: 'logistics-transport', serviceLabel: 'Logistics & Transportation',
    status: 'closed', priority: 'low', date: '2026-04-15', client: 'Youssef Kamal',
    company: 'Egyptian Drilling Co', country: 'egypt', countryLabel: 'Egypt', clientType: 'vendor',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: '1-2-weeks',
    duration: 'short-term', equipmentCount: 2, completeness: 60, responseTimeHours: 30,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: true, revisionRequests: 0,
  },
  {
    id: 'EP-8V0W2X', service: 'chemicals-supply', serviceLabel: 'Chemicals Supply',
    status: 'proposal-generated', priority: 'medium', date: '2026-04-22', client: 'Lisa Chen',
    company: 'Baker Hughes GCC', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'vendor',
    wellheadPressure: '', h2sPresence: 'yes', sandProduction: 'low', timeline: 'planned',
    duration: 'long-term', equipmentCount: 3, completeness: 82, responseTimeHours: 20,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-9Y1Z3A', service: 'surface-well-testing', serviceLabel: 'Surface Well Testing',
    status: 'submitted', priority: 'high', date: '2026-05-01', client: 'Nasser Al-Mutairi',
    company: 'KJO', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'operator',
    wellheadPressure: '8200', h2sPresence: 'yes', sandProduction: 'medium', timeline: 'immediate',
    duration: 'long-term', equipmentCount: 16, completeness: 94, responseTimeHours: 0,
    validationStatus: null, proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-0B2C4D', service: 'stimulation-nitrogen', serviceLabel: 'Stimulation & Nitrogen',
    status: 'validation-completed', priority: 'medium', date: '2026-04-26', client: 'Mark Davis',
    company: 'Weatherford UAE', country: 'uae', countryLabel: 'UAE', clientType: 'vendor',
    wellheadPressure: '5100', h2sPresence: 'no', sandProduction: 'low', timeline: '1-2-weeks',
    duration: 'short-term', equipmentCount: 5, completeness: 86, responseTimeHours: 26,
    validationStatus: 'Conditional', proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-1E3F5G', service: 'equipment-rental', serviceLabel: 'Equipment Rental',
    status: 'submitted', priority: 'low', date: '2026-04-30', client: 'Tariq Shaheen',
    company: 'Al Rajhi Industrial', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'epc',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: 'planned',
    duration: 'short-term', equipmentCount: 12, completeness: 55, responseTimeHours: 0,
    validationStatus: null, proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-2H4I6J', service: 'filtration-water', serviceLabel: 'Filtration & Water Treatment',
    status: 'under-review', priority: 'medium', date: '2026-04-27', client: 'Amira Soliman',
    company: 'PetroGulf Egypt', country: 'egypt', countryLabel: 'Egypt', clientType: 'operator',
    wellheadPressure: '1800', h2sPresence: 'no', sandProduction: 'high', timeline: '1-2-weeks',
    duration: 'long-term', equipmentCount: 6, completeness: 80, responseTimeHours: 32,
    validationStatus: null, proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-3K5L7M', service: 'surface-well-testing', serviceLabel: 'Surface Well Testing',
    status: 'closed', priority: 'high', date: '2026-04-03', client: 'Saeed Al-Hajri',
    company: 'QatarEnergy Ops', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'operator',
    wellheadPressure: '6800', h2sPresence: 'yes', sandProduction: 'medium', timeline: 'immediate',
    duration: 'long-term', equipmentCount: 12, completeness: 100, responseTimeHours: 6,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: true, revisionRequests: 0,
  },
  {
    id: 'EP-4N6O8P', service: 'fabrication-skid', serviceLabel: 'Fabrication & Skid Assembly',
    status: 'under-review', priority: 'high', date: '2026-04-28', client: 'John Williams',
    company: 'WorleyParsons', country: 'uae', countryLabel: 'UAE', clientType: 'epc',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: '1-2-weeks',
    duration: 'long-term', equipmentCount: 15, completeness: 75, responseTimeHours: 28,
    validationStatus: null, proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-5Q7R9S', service: 'chemicals-supply', serviceLabel: 'Chemicals Supply',
    status: 'submitted', priority: 'low', date: '2026-04-29', client: 'Ali Mohsen',
    company: 'Taqa Services', country: 'uae', countryLabel: 'UAE', clientType: 'vendor',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: 'planned',
    duration: 'short-term', equipmentCount: 2, completeness: 50, responseTimeHours: 0,
    validationStatus: null, proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-6T8U0V', service: 'logistics-transport', serviceLabel: 'Logistics & Transportation',
    status: 'proposal-generated', priority: 'medium', date: '2026-04-19', client: 'Ibrahim Khalaf',
    company: 'Saudi Logistics Hub', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'epc',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: '1-2-weeks',
    duration: 'short-term', equipmentCount: 6, completeness: 88, responseTimeHours: 18,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-7W9X1Y', service: 'surface-well-testing', serviceLabel: 'Surface Well Testing',
    status: 'validation-completed', priority: 'high', date: '2026-04-24', client: 'Peter Grant',
    company: 'TechnipFMC', country: 'uae', countryLabel: 'UAE', clientType: 'epc',
    wellheadPressure: '8900', h2sPresence: 'yes', sandProduction: 'high', timeline: 'immediate',
    duration: 'long-term', equipmentCount: 20, completeness: 96, responseTimeHours: 10,
    validationStatus: 'Conditional', proposalGenerated: false, clientResponded: false, revisionRequests: 0,
  },
  {
    id: 'EP-8Z0A2B', service: 'stimulation-nitrogen', serviceLabel: 'Stimulation & Nitrogen',
    status: 'closed', priority: 'medium', date: '2026-04-06', client: 'Karim Haddad',
    company: 'Petronas Carigali', country: 'egypt', countryLabel: 'Egypt', clientType: 'operator',
    wellheadPressure: '3800', h2sPresence: 'no', sandProduction: 'low', timeline: '1-2-weeks',
    duration: 'short-term', equipmentCount: 4, completeness: 90, responseTimeHours: 14,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: true, revisionRequests: 0,
  },
  {
    id: 'EP-9C1D3E', service: 'equipment-rental', serviceLabel: 'Equipment Rental',
    status: 'closed', priority: 'low', date: '2026-04-11', client: 'Ahmed Zaki',
    company: 'National Drilling Co', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'operator',
    wellheadPressure: '', h2sPresence: 'no', sandProduction: '', timeline: 'planned',
    duration: 'short-term', equipmentCount: 8, completeness: 70, responseTimeHours: 40,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: true, revisionRequests: 1,
  },
  {
    id: 'EP-0F2G4H', service: 'filtration-water', serviceLabel: 'Filtration & Water Treatment',
    status: 'proposal-generated', priority: 'medium', date: '2026-04-21', client: 'Fatima Al-Saud',
    company: 'SABIC Water Mgmt', country: 'ksa', countryLabel: 'Saudi Arabia', clientType: 'operator',
    wellheadPressure: '2200', h2sPresence: 'no', sandProduction: 'medium', timeline: '1-2-weeks',
    duration: 'long-term', equipmentCount: 9, completeness: 92, responseTimeHours: 15,
    validationStatus: 'Approved', proposalGenerated: true, clientResponded: false, revisionRequests: 0,
  },
];

// ─── COMPUTATION FUNCTIONS ────────────────────────────────────────────────────

// Commercial Performance
export function getStatusCounts() {
  const counts = { submitted: 0, 'under-review': 0, 'validation-completed': 0, 'proposal-generated': 0, 'pending-feedback': 0, closed: 0 };
  ANALYTICS_RFQS.forEach(r => { counts[r.status]++; });
  return counts;
}

export function getConversionFunnel() {
  const total = ANALYTICS_RFQS.length;
  const validated = ANALYTICS_RFQS.filter(r => r.validationStatus !== null).length;
  const proposalGenerated = ANALYTICS_RFQS.filter(r => r.proposalGenerated).length;
  const clientResponded = ANALYTICS_RFQS.filter(r => r.clientResponded).length;
  return [
    { stage: 'RFQ Submitted', count: total, pct: 100 },
    { stage: 'Validated', count: validated, pct: Math.round((validated / total) * 100) },
    { stage: 'Proposal Generated', count: proposalGenerated, pct: Math.round((proposalGenerated / total) * 100) },
    { stage: 'Client Response', count: clientResponded, pct: Math.round((clientResponded / total) * 100) },
  ];
}

export function getAverageResponseTime() {
  const responded = ANALYTICS_RFQS.filter(r => r.responseTimeHours > 0);
  const avg = responded.reduce((sum, r) => sum + r.responseTimeHours, 0) / responded.length;
  return Math.round(avg);
}

// Operational Demand
export function getRFQsByService() {
  const map: Record<string, number> = {};
  ANALYTICS_RFQS.forEach(r => { map[r.serviceLabel] = (map[r.serviceLabel] || 0) + 1; });
  return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

export function getRFQsByCountry() {
  const map: Record<string, number> = {};
  ANALYTICS_RFQS.forEach(r => { map[r.countryLabel] = (map[r.countryLabel] || 0) + 1; });
  return Object.entries(map).map(([name, value]) => ({ name, value }));
}

export function getRFQsByCondition() {
  const highPressure = ANALYTICS_RFQS.filter(r => parseInt(r.wellheadPressure) >= 5000).length;
  const h2s = ANALYTICS_RFQS.filter(r => r.h2sPresence === 'yes').length;
  const sand = ANALYTICS_RFQS.filter(r => r.sandProduction === 'high' || r.sandProduction === 'medium').length;
  return [
    { name: 'High Pressure (≥5000 PSI)', value: highPressure },
    { name: 'H₂S Presence', value: h2s },
    { name: 'Sand Production', value: sand },
  ];
}

// Quality & Behavior
export function getCompletenessMetrics() {
  const complete = ANALYTICS_RFQS.filter(r => r.completeness >= 80).length;
  const total = ANALYTICS_RFQS.length;
  return { complete, total, pct: Math.round((complete / total) * 100) };
}

export function getTotalRevisionRequests() {
  return ANALYTICS_RFQS.reduce((sum, r) => sum + r.revisionRequests, 0);
}

export function getClientTypeDistribution() {
  const map: Record<string, number> = { Operator: 0, EPC: 0, Vendor: 0 };
  ANALYTICS_RFQS.forEach(r => {
    if (r.clientType === 'operator') map['Operator']++;
    else if (r.clientType === 'epc') map['EPC']++;
    else map['Vendor']++;
  });
  return Object.entries(map).map(([name, value]) => ({ name, value }));
}

// Priority Scoring
export function calculatePriorityScore(rfq: AnalyticsRFQ): { score: number; level: 'high' | 'medium' | 'low'; factors: string[] } {
  let score = 0;
  const factors: string[] = [];

  // Client type weight
  if (rfq.clientType === 'operator') { score += 30; factors.push('Operator client (+30)'); }
  else if (rfq.clientType === 'epc') { score += 20; factors.push('EPC client (+20)'); }
  else { score += 10; factors.push('Vendor client (+10)'); }

  // Timeline urgency
  if (rfq.timeline === 'immediate') { score += 30; factors.push('Immediate timeline (+30)'); }
  else if (rfq.timeline === '1-2-weeks') { score += 20; factors.push('1-2 weeks timeline (+20)'); }
  else { score += 10; factors.push('Planned timeline (+10)'); }

  // Scope size (equipment count)
  if (rfq.equipmentCount >= 15) { score += 25; factors.push('Large scope ≥15 units (+25)'); }
  else if (rfq.equipmentCount >= 8) { score += 15; factors.push('Medium scope 8-14 units (+15)'); }
  else { score += 5; factors.push('Small scope <8 units (+5)'); }

  // Data completeness
  if (rfq.completeness >= 90) { score += 15; factors.push('High completeness ≥90% (+15)'); }
  else if (rfq.completeness >= 70) { score += 10; factors.push('Moderate completeness 70-89% (+10)'); }
  else { score += 5; factors.push('Low completeness <70% (+5)'); }

  const level = score >= 70 ? 'high' : score >= 45 ? 'medium' : 'low';
  return { score, level, factors };
}

export function getPriorityDistribution() {
  let high = 0, medium = 0, low = 0;
  ANALYTICS_RFQS.forEach(rfq => {
    const { level } = calculatePriorityScore(rfq);
    if (level === 'high') high++;
    else if (level === 'medium') medium++;
    else low++;
  });
  return [
    { name: 'High', value: high, color: '#ef4444' },
    { name: 'Medium', value: medium, color: '#f59e0b' },
    { name: 'Low', value: low, color: '#22c55e' },
  ];
}

export function getTopPriorityRFQs() {
  return ANALYTICS_RFQS
    .map(rfq => ({ ...rfq, ...calculatePriorityScore(rfq) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);
}
